import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useContext } from 'react';
import { Trans } from 'react-i18next';
import { formatDisplayDateWithFormat } from 'syf-js-utilities';
import { fontWeights } from 'syf-component-library/defaultTheme';
import { INELIGIBLE_REASON } from '../../../../const/paymentEnterpriseEligibility';
import MainContext from '../../../../Context';
import { getAutopayIneligibleCriteria, isSecuredInstallmentLoanProduct } from '../../../../helpers';
import { isEnrollIneligibleOutstandingDueAmount, isEnrollIneligibleWithin48HoursWindow } from '../../../../helpers/autopayHelpers';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import useAutopayBannerMachine from '../../../../hooks/machines/useAutopayBannerMachine';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import { t } from '../../../../strings/i18n';
const AUTOPAY_ENROLLED_STATE = 'enrollment.enrolled';
const AutopayBannerStatusMessage = () => {
    var _a;
    const { helpNumber, featureFlags } = useContext(MainContext);
    const [apiMachine] = useApiMachine();
    const { accountSummaryQueryResult: { data: { product: { type: productType } } }, autopayStatusQueryResult: { data: autopayStatus }, paymentEnterpriseEligibilityQueryResult: { data: paymentEnterpriseEligibility } } = apiMachine.context;
    const [autopayBannerMachine] = useAutopayBannerMachine();
    const isAutopayEnrolled = autopayBannerMachine.matches(AUTOPAY_ENROLLED_STATE);
    const [, paymentFlowRef] = usePaymentFlowMachine();
    const isUnenrollingSuccess = paymentFlowRef.context.successType === 'unenrolling';
    const autopayEnrollIneligibleReason = getAutopayIneligibleCriteria(paymentEnterpriseEligibility, 'AUTOPAY_ENROLL');
    const isIneligiblePriorFirstStatement = (autopayEnrollIneligibleReason === null || autopayEnrollIneligibleReason === void 0 ? void 0 : autopayEnrollIneligibleReason.value) ===
        INELIGIBLE_REASON.statementNotGenerated;
    const isAutopayDisabledForProduct = (autopayEnrollIneligibleReason === null || autopayEnrollIneligibleReason === void 0 ? void 0 : autopayEnrollIneligibleReason.value) ===
        INELIGIBLE_REASON.notEnabledForChannelOrProduct;
    const isIneligibleDelinquent = (autopayEnrollIneligibleReason === null || autopayEnrollIneligibleReason === void 0 ? void 0 : autopayEnrollIneligibleReason.value) ===
        INELIGIBLE_REASON.notEligibleDueToDelinquency;
    // Handle Banner Message - Enrollment ON
    if (isAutopayEnrolled) {
        // All Products Except Secured Installment
        if (!isSecuredInstallmentLoanProduct(productType)) {
            return _jsx(_Fragment, { children: t('autopayCollapseBanner.autopayCollapseBannerTextOn') });
        }
        // Secured Installment - Autopay Flag ON, Prepayment Details message
        if (featureFlags.isSecuredInstallmentAutopayEnabled) {
            const autopayDueDate = formatDisplayDateWithFormat((_a = autopayStatus === null || autopayStatus === void 0 ? void 0 : autopayStatus.enrollmentDetails) === null || _a === void 0 ? void 0 : _a.nextDueDate, 'MM/dd/yyyy');
            return (_jsx(Trans, { components: [_jsx("span", { style: { fontWeight: fontWeights.bold } })], i18nKey: "autopayCollapseBanner.prepaymentDetails", values: {
                    date: autopayDueDate,
                    helpNumber
                } }));
        }
        // Secured Installment - Autopay Flag OFF, Modify not supported message
        return (_jsx(_Fragment, { children: t('autopayCollapseBanner.autopayOnForSIAutopayEnabledFeatureFlagOff', {
                helpNumber
            }) }));
    }
    // Handle Banner Message - Enrollment OFF, Cancellation Success Screen
    if (isUnenrollingSuccess) {
        return _jsx(_Fragment, { children: t('autopayCollapseBanner.cancelAutopayConfirmationText') });
    }
    // Handle Banner Message - Enrollment OFF, Enrollment not Allowed for Product type
    if (isAutopayDisabledForProduct ||
        (isSecuredInstallmentLoanProduct(productType) &&
            !featureFlags.isSecuredInstallmentAutopayEnabled)) {
        return (_jsx(_Fragment, { children: t('autopayCollapseBanner.autopayBannerOffUnsupportedProduct', {
                helpNumber
            }) }));
    }
    // Handle Banner Message - Enrollment OFF, Enrollment not Allowed before statement cycle
    if (isIneligiblePriorFirstStatement) {
        return (_jsx(_Fragment, { children: t('autopayCollapseBanner.autopayBannerOffPriorFirstStatement', {
                helpNumber
            }) }));
    }
    // Handle Banner Message - Enrollment OFF, Enrollment not Allowed due to outstanding amount
    if (isEnrollIneligibleOutstandingDueAmount(paymentEnterpriseEligibility) ||
        isIneligibleDelinquent) {
        return (_jsx(_Fragment, { children: t('autopayCollapseBanner.autopayBannerOffOutstandingDueAmount') }));
    }
    // Handle Banner Message - Enrollment OFF, Enrollment not Allowed within 48 hours of due date
    if (isEnrollIneligibleWithin48HoursWindow(paymentEnterpriseEligibility)) {
        return _jsx(_Fragment, { children: t('autopayCollapseBanner.autopayBannerOffWithin48HourWindow') });
    }
    // Handle Banner Message - Enrollment OFF, Enrollment not Allowed for unknown reason
    if (autopayEnrollIneligibleReason) {
        return _jsx(_Fragment, { children: t('autopayCollapseBanner.autopayHelpNumber', { helpNumber }) });
    }
    // Handle Banner Message - Enrollment OFF, Enrollment Allowed
    return _jsx(_Fragment, { children: t('autopayCollapseBanner.autopayCollapseBannerTextOff') });
};
export default AutopayBannerStatusMessage;
